import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type AssignRoleInput = {
  roleId: string;
  userId: string;
};

// POST /api/settings/roles/:roleId/assign
//
// Replaces revalidatePath("/admin/settings/roles/${roleId}") +
// revalidatePath("/admin/settings/roles") from
// /app/admin/settings/roles/_actions.ts::assignRoleToUser. Also invalidates
// the internal-user list (each user row shows the assigned role) and the
// audit log (the action writes a domain-specific role.assign row).
export function useAssignRole() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, AssignRoleInput>({
    mutationFn: ({ roleId, userId }) =>
      apiFetch<{ ok: true }>(`/settings/roles/${roleId}/assign`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      }),
    onSuccess: (_data, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.role(roleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.roles() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.internal() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.audit() });
    },
    onError: (err) => {
      if (err.code === "user_not_internal") {
        toast.error("Only internal users can be assigned a role.");
        return;
      }
      if (err.code === "role_not_found" || err.code === "user_not_found") {
        toast.error("Role or user not found.");
        return;
      }
      toast.error(err.message ?? "Failed to assign role");
    },
  });
}
